import apiClient from './apiClient';

/**
 * Cache für Korrespondenz-Daten (Mieterkorrespondenz) mit Time-to-Live.
 * Reduziert redundante API-Aufrufe bei Navigation und Tab-Wechsel.
 *
 * Pattern analog zu utilityBillCacheService.js, aber mit:
 * - Kürzerer TTL (5min) da transaktionale Daten
 * - Expliziter Invalidierung nach Mutationen
 * - ETag-Support (304 Not Modified)
 * - localStorage-Persistenz (überlebt Page-Reload)
 * - Request-Deduplizierung (Cache-Stampede-Schutz)
 */
const CACHE_VERSION = 'v1';
const CACHE_PREFIX = `correspondences:${CACHE_VERSION}:`;
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 Minuten (transaktionale Daten)

// In-Memory Cache als Fallback (Private Mode, localStorage nicht verfügbar)
const memoryCache = new Map();
const pendingRequests = new Map();

// Invalidation-Token für Race-Condition-Schutz bei clearCache()
let cacheInvalidationToken = 0;

/**
 * Prüft ob localStorage verfügbar ist.
 * @returns {boolean}
 */
const isLocalStorageAvailable = () => {
  try {
    const testKey = '__corr_storage_test__';
    localStorage.setItem(testKey, testKey);
    localStorage.removeItem(testKey);
    return true;
  } catch {
    return false;
  }
};

const storageAvailable = isLocalStorageAvailable();

const isFresh = (entry) => {
  return !!entry && Date.now() - entry.timestamp < CACHE_TTL_MS;
};

/**
 * Liest Cache-Eintrag aus localStorage.
 * @param {string} key - Cache-Key (ohne Prefix)
 * @returns {object|null} Cache-Eintrag oder null wenn nicht vorhanden/ungültig
 */
const getFromLocalStorage = (key) => {
  if (!storageAvailable) return null;
  try {
    const item = localStorage.getItem(CACHE_PREFIX + key);
    if (!item) return null;
    const parsed = JSON.parse(item);
    if (!parsed || typeof parsed.timestamp !== 'number') return null;
    return parsed;
  } catch {
    return null;
  }
};

/**
 * Schreibt Cache-Eintrag in localStorage.
 * @param {string} key - Cache-Key (ohne Prefix)
 * @param {object} value - Cache-Wert { data, timestamp, etag }
 */
const setToLocalStorage = (key, value) => {
  if (!storageAvailable) return;
  try {
    localStorage.setItem(CACHE_PREFIX + key, JSON.stringify(value));
  } catch {
    // Quota exceeded — Fallback auf Memory-Cache
  }
};

const removeFromLocalStorage = (key) => {
  if (!storageAvailable) return;
  try {
    localStorage.removeItem(CACHE_PREFIX + key);
  } catch {
    // Ignorieren bei Fehlern
  }
};

const invalidateCacheKey = (key) => {
  memoryCache.delete(key);
  pendingRequests.delete(key);
  removeFromLocalStorage(key);
};

const wrapSharedPromiseForCaller = (sharedPromise, signal) => {
  if (!signal) {
    return sharedPromise;
  }

  if (signal.aborted) {
    const abortError = new Error('Request aborted');
    abortError.name = 'AbortError';
    return Promise.reject(abortError);
  }

  return new Promise((resolve, reject) => {
    const handleAbort = () => {
      signal.removeEventListener('abort', handleAbort);
      const abortError = new Error('Request aborted');
      abortError.name = 'AbortError';
      reject(abortError);
    };

    signal.addEventListener('abort', handleAbort, { once: true });

    sharedPromise.then(
      (value) => {
        signal.removeEventListener('abort', handleAbort);
        resolve(value);
      },
      (error) => {
        signal.removeEventListener('abort', handleAbort);
        reject(error);
      }
    );
  });
};

/**
 * Löscht alle Cache-Einträge aus localStorage.
 */
const clearLocalStorageCache = () => {
  if (!storageAvailable) return;
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key && key.startsWith(CACHE_PREFIX)) {
        localStorage.removeItem(key);
      }
    }
  } catch {
    // Ignorieren bei Fehlern
  }
};

/**
 * Generische Cache-Funktion für GET-Requests auf Correspondence-Endpoints.
 * Unterstützt localStorage-Persistenz und ETag-basierte Revalidierung.
 * @param {string} url - API-Pfad (z.B. '/correspondences/grouped')
 * @param {object} [params] - Query-Parameter
 * @param {AbortSignal} [signal] - AbortController-Signal
 * @param {number} [_retryCount=0] - Interner Retry-Zähler
 * @returns {Promise<Array|object>} Die Daten (defensive Kopie bei Arrays)
 */
const getCached = (url, params, signal, _retryCount = 0) => {
  const cacheKey = params ? `${url}?${JSON.stringify(params)}` : url;

  // 1. Prüfe Memory-Cache (schnellster Pfad)
  const memoryCached = memoryCache.get(cacheKey);
  if (isFresh(memoryCached)) {
    return Promise.resolve(Array.isArray(memoryCached.data) ? [...memoryCached.data] : memoryCached.data);
  }
  if (memoryCached) {
    memoryCache.delete(cacheKey);
  }

  // 2. Prüfe localStorage-Cache
  const localCached = getFromLocalStorage(cacheKey);
  if (isFresh(localCached)) {
    memoryCache.set(cacheKey, localCached);
    return Promise.resolve(Array.isArray(localCached.data) ? [...localCached.data] : localCached.data);
  }
  if (localCached) {
    removeFromLocalStorage(cacheKey);
  }

  // 3. Deduplizierung: laufenden Request wiederverwenden
  if (pendingRequests.has(cacheKey)) {
    return wrapSharedPromiseForCaller(
      pendingRequests.get(cacheKey).then(
        data => Array.isArray(data) ? [...data] : data
      ),
      signal
    );
  }

  // 4. API-Request mit ETag-Support
  const config = {
    validateStatus: (status) => status === 200 || status === 304
  };
  if (params) config.params = params;

  // ETag senden falls vorhanden (Revalidierung)
  const staleEntry = localCached || memoryCached;
  const etag = staleEntry?.etag;
  if (etag) {
    config.headers = { 'If-None-Match': etag };
  }

  const currentToken = cacheInvalidationToken;

  const promise = apiClient.get(url, config).then(response => {
    // RACE-SCHUTZ: Nicht cachen wenn clearCache()/invalidate aufgerufen wurde
    if (currentToken !== cacheInvalidationToken) {
      pendingRequests.delete(cacheKey);
      if (response.status === 304) {
        const cachedData = staleEntry?.data;
        if (cachedData) return cachedData;
        if (_retryCount < 1 && !signal?.aborted) {
          return getCached(url, params, signal, _retryCount + 1);
        }
        return Array.isArray(response.data) ? [...response.data] : (response.data || []);
      }
      return Array.isArray(response.data) ? [...response.data] : response.data;
    }

    if (response.status === 304) {
      // 304 Not Modified: Cached Daten nutzen, Timestamp aktualisieren
      if (!staleEntry) {
        pendingRequests.delete(cacheKey);
        invalidateCacheKey(cacheKey);
        if (_retryCount < 1 && !signal?.aborted) {
          return getCached(url, params, signal, _retryCount + 1);
        }
        return [];
      }

      const refreshedEntry = {
        data: staleEntry.data,
        timestamp: Date.now(),
        etag: staleEntry.etag
      };
      memoryCache.set(cacheKey, refreshedEntry);
      setToLocalStorage(cacheKey, refreshedEntry);
      pendingRequests.delete(cacheKey);
      return staleEntry.data;
    }

    // 200 OK: Neue Daten cachen
    const data = response.data;
    const responseEtag = response.headers['etag'];
    const newEntry = {
      data,
      timestamp: Date.now(),
      etag: responseEtag
    };
    memoryCache.set(cacheKey, newEntry);
    setToLocalStorage(cacheKey, newEntry);
    pendingRequests.delete(cacheKey);
    return data;
  }).catch(err => {
    pendingRequests.delete(cacheKey);
    throw err;
  });

  pendingRequests.set(cacheKey, promise);

  return wrapSharedPromiseForCaller(
    promise.then(data => Array.isArray(data) ? [...data] : data),
    signal
  );
};

const correspondenceCacheService = {
  /**
   * Lädt alle Korrespondenzen gruppiert nach Mietvertrag (gecacht).
   * @param {AbortSignal} [signal] - AbortController-Signal
   * @returns {Promise<Array>} Liste der ContractCorrespondenceGroupDTOs
   */
  getCorrespondencesGrouped: (signal) => getCached('/correspondences/grouped', undefined, signal),

  /**
   * Lädt eine einzelne Korrespondenz (gecacht).
   * @param {string} id - UUID der Korrespondenz
   * @param {AbortSignal} [signal] - AbortController-Signal
   * @returns {Promise<object>} CorrespondenceDTO
   */
  getCorrespondence: (id, signal) => getCached(`/correspondences/${id}`, undefined, signal),

  /**
   * Invalidiert den gruppierten List-Cache (nach Create, Update, Delete, Send).
   */
  invalidateAll: () => {
    cacheInvalidationToken++;
    memoryCache.clear();
    pendingRequests.clear();
    clearLocalStorageCache();
  },

  /**
   * Löscht den kompletten Cache (bei Logout).
   */
  clearCache: () => {
    cacheInvalidationToken++;
    memoryCache.clear();
    pendingRequests.clear();
    clearLocalStorageCache();
  },

  /**
   * Generiert eine Vorschau des Mieterhöhungstexts.
   * Nicht gecacht, da es sich um einen Preview/Compute-Call handelt.
   * @param {object} data - { rentalContractId, reason, newBaseRent, newAdditionalCosts, effectiveDate }
   * @param {AbortSignal} [signal]
   * @returns {Promise<object>} { generatedText: string }
   */
  previewRentIncreaseText: (data, signal) =>
    apiClient.post('/correspondences/rent-increase/preview-text', data, { signal }).then(r => r.data),

  /**
   * Validiert das Datum einer Mieterhöhung (12-Monats-Regel).
   * Nicht gecacht, da es sich um einen Validierungs-Call handelt.
   * @param {object} params - { rentalContractId, effectiveDate }
   * @param {AbortSignal} [signal]
   * @returns {Promise<object>} { valid: boolean, earliestDate: string, message: string }
   */
  validateRentIncreaseDate: (params, signal) =>
    apiClient.get('/correspondences/rent-increase/validate-date', { params, signal }).then(r => r.data),

  /**
   * Generiert eine Vorschau des Anpassungstexts für die Nebenkostenvorauszahlung.
   * Nicht gecacht, da es sich um einen Preview/Compute-Call handelt.
   * @param {object} data - { rentalContractId, newAdditionalCosts, effectiveDate }
   * @param {AbortSignal} [signal]
   * @returns {Promise<object>} { generatedText: string, landlordDisplayName: string }
   */
  previewAdditionalCostsAdjustmentText: (data, signal) =>
    apiClient.post('/correspondences/additional-costs-adjustment/preview-text', data, { signal }).then(r => r.data),

  /**
   * Generiert eine Vorschau des Ankündigungstexts für Reparaturen.
   * Nicht gecacht, da es sich um einen Preview/Compute-Call handelt.
   * @param {object} data - { rentalContractId, effectiveDate, repairItems }
   * @param {AbortSignal} [signal]
   * @returns {Promise<object>} { generatedText: string, landlordDisplayName: string }
   */
  previewRepairAnnouncementText: (data, signal) =>
    apiClient.post('/correspondences/repair-announcement/preview-text', data, { signal }).then(r => r.data),

  /**
   * Generiert eine Vorschau des Ankündigungstexts für Modernisierungsmaßnahmen.
   * Nicht gecacht, da es sich um einen Preview/Compute-Call handelt.
   * @param {object} data - { rentalContractId, startDate, durationValue, durationType, expectedImpact,
   *                          expectedRentIncrease, operatingCostChangeType, operatingCostChangeAmount, measures }
   * @param {AbortSignal} [signal]
   * @returns {Promise<object>} { generatedText: string, landlordDisplayName: string }
   */
  previewModernizationAnnouncementText: (data, signal) =>
    apiClient.post('/correspondences/modernization-announcement/preview-text', data, { signal }).then(r => r.data),

  /**
   * Generiert eine Vorschau des Abmahnungstexts wegen Pflichtverletzung.
   * Nicht gecacht, da es sich um einen Preview/Compute-Call handelt.
   * @param {object} data - { rentalContractId, pflichtVerletzungen }
   * @param {AbortSignal} [signal]
   * @returns {Promise<object>} { generatedText: string, landlordDisplayName: string }
   */
  previewAbmahnungText: (data, signal) =>
    apiClient.post('/correspondences/abmahnung/preview-text', data, { signal }).then(r => r.data),

  /**
   * Generiert eine Vorschau des Mahnungstexts wegen Zahlungsverzug.
   * Nicht gecacht, da es sich um einen Preview/Compute-Call handelt.
   * @param {object} data - { rentalContractId, kuendigungsAndrohung, zahlungsverzuege }
   * @param {AbortSignal} [signal]
   * @returns {Promise<object>} { generatedText: string, landlordDisplayName: string }
   */
  previewMahnungZahlungsverzugText: (data, signal) =>
    apiClient.post('/correspondences/mahnung-zahlungsverzug/preview-text', data, { signal }).then(r => r.data),

  /**
   * Generiert eine Vorschau des Kündigungstexts.
   * Nicht gecacht, da es sich um einen Preview/Compute-Call handelt.
   * @param {object} data - { rentalContractId, kuendigungsArt, kuendigungsGrund, noticeMonths?,
   *                          effectiveTerminationDate?, kuendigungsDatum?, freeText? }
   * @param {AbortSignal} [signal]
   * @returns {Promise<object>} { generatedText: string, suggestedNoticeMonths: number|null,
   *                              suggestedEffectiveTerminationDate: string|null }
   */
  previewKuendigungText: (data, signal) =>
    apiClient.post('/correspondences/kuendigung/preview-text', data, { signal }).then(r => r.data),

  /**
   * Generiert eine Vorschau des Terminvereinbarungstexts.
   * Nicht gecacht, da es sich um einen Preview/Compute-Call handelt.
   * @param {object} data - { rentalContractId, anlass, vorschlaege: [{ vorschlagsDatum, vorschlagsZeit }] }
   * @param {AbortSignal} [signal]
   * @returns {Promise<object>} { generatedText: string }
   */
  previewTerminvereinbarungText: (data, signal) =>
    apiClient.post('/correspondences/terminvereinbarung/preview-text', data, { signal }).then(r => r.data),

  /**
   * Generiert eine Vorschau des Sonstiges-Texts.
   * Nicht gecacht, da es sich um einen Preview/Compute-Call handelt.
   * @param {object} data - { rentalContractId, bodyText }
   * @param {AbortSignal} [signal]
   * @returns {Promise<object>} { generatedText: string }
   */
  previewSonstigesText: (data, signal) =>
    apiClient.post('/correspondences/sonstiges/preview-text', data, { signal }).then(r => r.data),

  /**
   * Generiert eine Vorschau des Bankverbindungsaenderungs-Texts.
   * Nicht gecacht, da es sich um einen Preview/Compute-Call handelt.
   * @param {object} data - { rentalContractId, newBankAccountId, effectiveDate? }
   * @param {AbortSignal} [signal]
   * @returns {Promise<object>} { generatedText: string, landlordDisplayName: string }
   */
  previewBankverbindungAenderungText: (data, signal) =>
    apiClient.post('/correspondences/bankverbindung-aenderung/preview-text', data, { signal }).then(r => r.data),

  /**
   * Generiert eine Vorschau des Zählerstandsübermittlungs-Texts.
   * Nicht gecacht, da es sich um einen Realtime-Preview-Call handelt.
   * @param {object} data - { rentalContractId, zaehlerId, dueDate?, freeText? }
   * @param {AbortSignal} [signal]
   * @returns {Promise<object>} { generatedText: string }
   */
  previewZaehlerstandUebermittlungText: (data, signal) =>
    apiClient.post('/correspondences/zaehlerstand-uebermittlung/preview-text', data, { signal }).then(r => r.data),

  /**
   * Laedt die verfügbaren Zähler für eine Zählerstandsübermittlungs-Korrespondenz.
   * Nicht gecacht, da kontextspezifische Daten pro Mietvertrag.
   * @param {string} rentalContractId - UUID des Mietvertrags
   * @param {AbortSignal} [signal]
   * @returns {Promise<Array>} MeterOptionDTO[] = [{ id, label, art, ort, realEstateLabel, realEstateType }]
   */
  getAvailableMetersForCorrespondence: (rentalContractId, signal) =>
    apiClient.get('/correspondences/zaehlerstand-uebermittlung/available-meters', {
      params: { rentalContractId },
      signal
    }).then(r => r.data),

  /**
   * Laedt die verfügbaren aktiven Mietverträge für eine Zählerstandsabfrage bei einem Zähler.
   * Nicht gecacht, da kontextspezifische Daten pro Zähler.
   * Bei Property-Zähler: aktive Verträge dieser Mieteinheit.
   * Bei PropertyGroup-Zähler: aktive Verträge aller Mieteinheiten der Liegenschaft.
   * @param {string} zaehlerId - UUID des Zählers
   * @param {AbortSignal} [signal]
   * @returns {Promise<Array>} RentalContractOptionDTO[] = [{ id, contractNumber, label, propertyId, propertyLabel, renterNames }]
   */
  getAvailableContractsForMeter: (zaehlerId, signal) =>
    apiClient.get('/correspondences/zaehlerstand-uebermittlung/available-contracts', {
      params: { zaehlerId },
      signal
    }).then(r => r.data)
};

export default correspondenceCacheService;
